<template>
  <v-dialog
    :model-value="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    max-width="640"
    scrollable
  >
    <v-card rounded="xl">
      <v-card-title class="d-flex align-center justify-space-between pa-5 pb-2">
        <div>
          <div class="dialog-title">Approved Requirements</div>
          <div class="dialog-subtitle">
            {{ student?.name }}
            <span v-if="student?.program_name"
              >· {{ student.program_name }}</span
            >
          </div>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="close"
        ></v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-5" style="max-height: 480px">
        <div v-if="loading">
          <v-skeleton-loader
            v-for="n in 3"
            :key="n"
            type="list-item-two-line"
          />
        </div>

        <div v-else-if="requirements.length > 0">
          <v-card
            v-for="(r, idx) in requirements"
            :key="r.submission?.id || idx"
            flat
            rounded="lg"
            class="requirement-item pa-3 mb-3"
          >
            <div class="d-flex align-center justify-space-between ga-3">
              <div class="d-flex align-center ga-3" style="min-width: 0">
                <div class="requirement-icon">
                  <v-icon color="#00a63e" size="20">
                    mdi-file-check-outline
                  </v-icon>
                </div>
                <div style="min-width: 0">
                  <div class="requirement-name">
                    {{ (r.requirement_name || "").trim() }}
                  </div>
                  <div class="requirement-meta">
                    <span v-if="r.submission?.approved_date">
                      Approved {{ formatDate(r.submission.approved_date) }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="d-flex align-center ga-1 flex-shrink-0">
                <v-chip
                  size="x-small"
                  color="success"
                  variant="tonal"
                  class="mr-1"
                >
                  Approved
                </v-chip>
                <v-btn
                  v-if="r.submission?.filename"
                  @click="viewFile(r)"
                  :loading="viewingId === r.submission.id"
                  :disabled="viewingId !== null"
                  color="primary"
                  variant="tonal"
                  size="small"
                  rounded="lg"
                >
                  <v-icon size="14" class="mr-1">mdi-eye-outline</v-icon>
                  View File
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>

        <div v-else class="text-center pa-8">
          <v-icon size="48" color="grey-lighten-1">
            mdi-file-document-outline
          </v-icon>
          <div
            class="font-weight-bold mt-3"
            style="font-size: 15px; color: #374151"
          >
            No approved requirements
          </div>
          <div class="text-grey mt-1" style="font-size: 13px">
            Approved submissions from this intern will appear here.
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <div class="count-label" v-if="requirements.length > 0">
          {{ requirements.length }} approved
          {{ requirements.length === 1 ? "requirement" : "requirements" }}
        </div>
        <v-spacer />
        <v-btn variant="text" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ViewApprovedRequirementsDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    student: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  data: () => ({
    loading: false,
    viewingId: null,
    requirements: [],
  }),
  watch: {
    modelValue(val) {
      if (val && this.student) {
        this.loadRequirements();
      } else if (!val) {
        this.requirements = [];
      }
    },
  },
  methods: {
    loadRequirements() {
      this.loading = true;
      this.axiosCall(
        "/student/getStudentApprovedRequirement/" + this.student.id,
        "GET",
      )
        .then((res) => {
          this.requirements = res?.data?.requirements || [];
        })
        .catch(() => {
          this.requirements = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    viewFile(item) {
      const filename = item?.submission?.filename;
      if (!filename) return;

      const url =
        process.env.VUE_APP_SERVER +
        "/student-requirement-submission/get-file/" +
        encodeURIComponent(filename);
      const token = localStorage.getItem("token");

      // Open the tab synchronously on click so popup blockers allow it
      const tab = window.open("about:blank");
      this.viewingId = item.submission.id;

      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to load file");
          return response.blob();
        })
        .then((blob) => {
          const objectUrl = window.URL.createObjectURL(blob);
          if (tab) {
            tab.location = objectUrl;
            tab.focus();
          }
        })
        .catch((err) => {
          console.log(err);
          if (tab) tab.close();
        })
        .finally(() => {
          this.viewingId = null;
        });
    },

    formatDate(value) {
      // "2026-10-24 07:03:23" -> ISO-friendly for all browsers
      const d = new Date(String(value).replace(" ", "T"));
      if (isNaN(d)) return value;
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    close() {
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.3px;
}
.dialog-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
}
.requirement-item {
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.requirement-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background-color: #e8f7ee;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.requirement-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.requirement-meta {
  font-size: 12px;
  color: #6b7280;
}
.count-label {
  font-size: 12px;
  color: #6b7280;
  padding-left: 8px;
}
</style>
